import type { MagicLayout } from '@magic/layout';

/** Registered id of the popup skeleton — the child of the UI's `spine_popup` slot. */
const POPUP_SPINE_ID = 'popup';

/**
 * Which of the popup's two buttons the situation offers, named after the skeleton
 * animation that poses them: the export ships one animation per combination, each
 * moving the button it does not offer out of the panel.
 *
 * - `only_close` — the corner X alone, for a popup that is only read and closed
 *   (the rules page).
 * - `only_accept` — the labelled button alone, for one that is only acknowledged
 *   (the round's reports: a free spins trigger has nothing to decline).
 * - `close_and_accept` — both, for an offer the player can turn down (buy bonus).
 */
export type PopupLayout = 'only_close' | 'only_accept' | 'close_and_accept';

/** Which of the popup's buttons the player dismissed it with. */
export type PopupResult = 'accept' | 'close';

/**
 * The button clicks that dismiss the popup, grouped by what they mean: the labelled
 * button along its bottom (the `button_accept_popup` bone, carrying the shared
 * big_button) accepts, the corner X (the `button_close_popup` slot) closes.
 *
 * Both are always listened for, whatever the layout — a skeleton that ships only one
 * of them never fires the other, and the layout is what puts the rest out of reach.
 *
 * The fade itself stays with the export: the UI keys `hide_popup` on each of these
 * (`event_accept_popup_click/hide_popup`, `event_close_popup_click/hide_popup`), so a
 * new button needs its own group there as well as its name here.
 */
const DISMISS_EVENTS: Record<PopupResult, string[]> = {
    // `popup_close_click` is what the labelled button was called before it split
    // into accept and close — kept so a skeleton exported before the split still
    // dismisses, and back then that button was an acknowledgement too
    accept: ['accept_popup_click', 'popup_close_click'],
    close: ['close_popup_click'],
};

/**
 * The track the layout animation is held on.
 *
 * It is a pose rather than a move — the popup keeps it for as long as it is open —
 * so it needs a track of its own: one from the range `AnimationsController`
 * allocates (it counts up from 0) gets recycled by the next animation that plays on
 * the popup, and the button the layout put away would slide back in on the first
 * press of the other one. Above the button feedback track (10) for the same reason.
 */
const LAYOUT_TRACK = 11;

/** The texts a popup shows: its heading, its body line, and its button's label. */
export type PopupText = {
    title?: string;
    content?: string;
    /** Label of the accept button — the corner X carries no text of its own. */
    buttonText?: string;
    /**
     * Count the content up from zero as the popup opens. On by default — the
     * round popups all reveal a figure the player just won. Off for a value that
     * was already decided before the popup opened, like a price.
     */
    animateContent?: boolean;
};

/** What a popup shows, and which of its buttons the situation offers. */
export type PopupOptions = PopupText & { layout: PopupLayout };

/**
 * The game's popup: one skeleton with a title, a content line, an accept button and
 * a close X, faded in by the `popup` state and dismissed by either button (see
 * {@link DISMISS_EVENTS}). Every popup goes through here — the round's presentation
 * popups (free spins, big win), the rules page and the buy bonus offer alike — so
 * they can't be open at the same time, and each says which of the two buttons its
 * situation offers (see {@link PopupLayout}).
 */
export class PopupController {
    constructor(private spine: MagicLayout) {}

    /**
     * Shows the popup with the given texts and buttons, and resolves with the button
     * the player dismissed it with — `accept` for the labelled one, `close` for the
     * corner X. A layout offering only one of them can only resolve with that one.
     *
     * The listeners are one-shot: the buttons belong to the shared popup, so one left
     * registered would also fire for whatever popup opens next.
     */
    async show(options: PopupOptions): Promise<PopupResult> {
        const { layout, title, content, buttonText, animateContent = true } = options;

        if (title) this.spine.texts.set('popup_title', title);
        if (content) {
            if (animateContent) {
                this.spine.texts.set('popup_content', '0');
                this.spine.texts.set('popup_content', content, true);
            } else {
                this.spine.texts.set('popup_content', content);
            }
        }
        if (buttonText) this.spine.texts.set('button_popup_close', buttonText);

        this.takePointerPriority();
        this.poseButtons(layout);

        await this.spine.animations.playState(`popup`);

        return new Promise((resolve) => {
            const listeners: { event: string; listener: () => void }[] = [];

            const dismiss = (result: PopupResult) => () => {
                listeners.forEach(({ event, listener }) =>
                    this.spine.animations.removeEventListener(event, listener),
                );

                // drop show_popup's lingering end pose: depending on what else
                // was animating when the popup opened, hide_popup can land on a
                // lower track and the stale pose above it would mask the close
                // animation (listeners run before playEvent starts hide_popup,
                // so the track is free by the time it plays)
                this.spine.animations.stopState('popup', false);
                resolve(result);
            };

            (Object.keys(DISMISS_EVENTS) as PopupResult[]).forEach((result) => {
                const listener = dismiss(result);

                DISMISS_EVENTS[result].forEach((event) => {
                    listeners.push({ event, listener });
                    this.spine.animations.addEventListener(event, listener);
                });
            });
        });
    }

    /**
     * Poses the popup's buttons for this situation — played before the fade-in, so
     * the player never sees a button they aren't offered slide out of the panel.
     *
     * The pose is left on its track once the popup closes: it has to outlast the
     * fade-out — clearing it there would pop the put-away button back into view
     * while the panel is still visible — and the next popup replaces it with its own.
     */
    private poseButtons(layout: PopupLayout) {
        const popup = this.spine.getSpine(POPUP_SPINE_ID);

        // an export from before the buttons split ships none of the layouts: it has
        // the one labelled button and nothing to pose, so it opens as it is
        if (!popup?.state.data.skeletonData.findAnimation(layout)) {
            console.warn(`Popup skeleton has no "${layout}" animation — buttons left as posed`);
            return;
        }

        this.spine.animations.play(POPUP_SPINE_ID, layout, false, LAYOUT_TRACK);
    }

    /**
     * Makes the popup the first thing pointer events are offered to.
     *
     * Pixi hit-tests a spine's children in reverse insertion order, and the layout
     * seeds that order from the skeleton's draw order — which puts the UI's spin
     * button label *after* the `spine_popup` slot. That label lands right on top of
     * the popup's own button, so without this it swallows every click meant for it
     * and the popup can't be dismissed at all.
     *
     * Nothing is restored on close: a closed popup is invisible, and Pixi skips
     * invisible containers when hit-testing, so it can't take a click it shouldn't.
     */
    private takePointerPriority() {
        const popup = this.spine.getSpine(POPUP_SPINE_ID);
        const parent = popup?.parent;

        if (!popup || !parent) return;

        parent.setChildIndex(popup, parent.children.length - 1);
    }
}
